/**
 * Global state stores using Zustand.
 * Cart uses the server-side proxy (draft orders via Admin API).
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  createCart,
  updateCart,
  fetchCart,
  getCheckoutUrl,
  type Cart,
  type CartLineItem,
} from '../utils/shopify'
import { customerApi, loginUrl, logoutUrl, type Customer } from '../utils/customer'

// ─── Cart Store ───────────────────────────────────────────────────────────────
interface CartStore {
  cartId: number | null
  checkoutUrl: string | null
  lines: CartLineItem[]
  subtotal: string
  currency: string
  isOpen: boolean
  loading: boolean
  initCart: () => Promise<void>
  addItem: (variantId: number, quantity: number) => Promise<void>
  updateItem: (variantId: number, quantity: number) => Promise<void>
  removeItem: (variantId: number) => Promise<void>
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  itemCount: () => number
  checkout: () => Promise<void>
}

function cartToState(cart: Cart) {
  return {
    cartId: cart.id,
    lines: cart.lineItems,
    subtotal: cart.subtotalPrice || '0.00',
    currency: cart.currencyCode || 'CAD',
  }
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      cartId: null,
      checkoutUrl: null,
      lines: [],
      subtotal: '0.00',
      currency: 'CAD',
      isOpen: false,
      loading: false,

      initCart: async () => {
        const { cartId } = get()
        if (!cartId) return
        try {
          const cart = await fetchCart(cartId)
          set(cartToState(cart))
        } catch {
          set({ cartId: null, checkoutUrl: null, lines: [], subtotal: '0.00' })
        }
      },

      addItem: async (variantId, quantity) => {
        set({ loading: true })
        try {
          const { cartId, lines } = get()
          // Build updated items list
          const existing = lines.find(l => l.variantId === variantId)
          let newItems: { variantId: number; quantity: number }[]
          if (existing) {
            newItems = lines.map(l =>
              l.variantId === variantId
                ? { variantId: l.variantId, quantity: l.quantity + quantity }
                : { variantId: l.variantId, quantity: l.quantity }
            )
          } else {
            newItems = [
              ...lines.map(l => ({ variantId: l.variantId, quantity: l.quantity })),
              { variantId, quantity },
            ]
          }

          let cart: Cart
          if (!cartId) {
            cart = await createCart(newItems)
          } else {
            cart = await updateCart(cartId, newItems)
          }
          set({ ...cartToState(cart), isOpen: true })
        } finally {
          set({ loading: false })
        }
      },

      updateItem: async (variantId, quantity) => {
        const { cartId, lines } = get()
        if (!cartId) return
        set({ loading: true })
        try {
          const newItems = lines
            .map(l => ({ variantId: l.variantId, quantity: l.variantId === variantId ? quantity : l.quantity }))
            .filter(l => l.quantity > 0)
          const cart = await updateCart(cartId, newItems)
          set(cartToState(cart))
        } finally {
          set({ loading: false })
        }
      },

      removeItem: async (variantId) => {
        const { cartId, lines } = get()
        if (!cartId) return
        set({ loading: true })
        try {
          const newItems = lines
            .filter(l => l.variantId !== variantId)
            .map(l => ({ variantId: l.variantId, quantity: l.quantity }))
          const cart = await updateCart(cartId, newItems)
          set(cartToState(cart))
        } finally {
          set({ loading: false })
        }
      },

      checkout: async () => {
        const { cartId } = get()
        if (!cartId) return
        try {
          const url = await getCheckoutUrl(cartId)
          if (url) window.location.href = url
        } catch (err) {
          console.error('Checkout error:', err)
        }
      },

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set((s) => ({ isOpen: !s.isOpen })),
      itemCount: () => get().lines.reduce((sum, l) => sum + l.quantity, 0),
    }),
    {
      name: 'matryxlab-cart-v3',
      partialize: (state) => ({ cartId: state.cartId }),
    }
  )
)

// ─── Auth Store ───────────────────────────────────────────────────────────────
// Backed by a server-side session cookie; sign-in happens on Shopify's hosted
// login page (Customer Account API). Nothing sensitive is persisted here.
interface AuthStore {
  customer: Customer | null
  isAuthenticated: boolean
  initialized: boolean
  email: string | null
  firstName: string | null
  lastName: string | null
  init: () => Promise<void>
  /** Redirects the browser to Shopify sign-in; comes back to `returnTo`. */
  login: (returnTo?: string) => void
  /** Clears the session and redirects through Shopify's logout. */
  logout: () => void
  setCustomer: (customer: Customer | null) => void
}

function customerToState(customer: Customer | null) {
  return {
    customer,
    isAuthenticated: !!customer,
    email: customer?.email ?? null,
    firstName: customer?.firstName ?? null,
    lastName: customer?.lastName ?? null,
  }
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  customer: null,
  isAuthenticated: false,
  initialized: false,
  email: null,
  firstName: null,
  lastName: null,

  init: async () => {
    if (get().initialized) return
    try {
      const { customer } = await customerApi.me()
      set(customerToState(customer))
    } catch {
      set(customerToState(null))
    } finally {
      set({ initialized: true })
    }
  },

  login: (returnTo = window.location.pathname) => {
    window.location.href = loginUrl(returnTo)
  },

  logout: () => {
    set(customerToState(null))
    window.location.href = logoutUrl()
  },

  setCustomer: (customer) => set(customerToState(customer)),
}))

// ─── UI Store ─────────────────────────────────────────────────────────────────
interface UIStore {
  searchQuery: string
  setSearchQuery: (q: string) => void
  mobileMenuOpen: boolean
  setMobileMenuOpen: (open: boolean) => void
}

export const useUIStore = create<UIStore>()((set) => ({
  searchQuery: '',
  setSearchQuery: (q) => set({ searchQuery: q }),
  mobileMenuOpen: false,
  setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
}))
